import React, { ComponentProps, forwardRef, memo } from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { Colors, FontSizes } from '@surglogs-app/ui.theme'

import {
  TypographyColorVariant,
  getColorKeyFromColorVariant,
} from '../services/TypographyService'
import {
  StyleProps,
  getCSSObjectFromStyleProps,
} from '../styleProps/StylePropsService'

export interface TextProps
  extends Omit<ComponentProps<typeof StyledText>, 'variant' | 'size'> {
  bold?: boolean
  italic?: boolean
  variant?: TypographyColorVariant
  size?: FontSizes
  /** Shorthand for `as: 'p'`, `false` by default, when `true` `p` is rendered */
  paragraph?: boolean
  /** Decides whether Text should be a inline-block element (which can use margins), `false` by default */
  block?: boolean
}

interface TextStyleProps extends StyleProps {
  bold?: boolean
  italic?: boolean
  variant: Colors
  size: FontSizes
}

export const StyledText = styled.span<TextStyleProps>`
  font-family: ${({ theme }) => theme.ui.typography.fontFamilies.default};
  font-size: ${({ theme, size }) => theme.ui.typography.fontSizes[size]};
  line-height: ${({ theme, size }) => theme.ui.typography.lineHeights[size]};
  color: ${({ theme, variant }) => theme.ui.colors[variant]};
  font-weight: ${({ theme, bold }) =>
    bold
      ? theme.ui.typography.fontWeights.semiBold
      : theme.ui.typography.fontWeights.regular};

  ${({ italic }) =>
    italic &&
    css`
      font-style: italic;
    `}

  ${getCSSObjectFromStyleProps}
`

export const Text = memo(
  forwardRef<HTMLSpanElement | HTMLParagraphElement, TextProps>(
    (
      {
        variant = 'default',
        size = 'sm',
        bold = false,
        italic = false,
        paragraph = false,
        block = false,
        display = block ? 'inline-block' : undefined,
        as,
        ...props
      },
      ref,
    ) => {
      return (
        <StyledText
          {...props}
          {...{
            as: as ?? (paragraph ? 'p' : 'span'),
            ref,
            size,
            bold,
            italic,
            variant: getColorKeyFromColorVariant(variant),
            display,
          }}
        />
      )
    },
  ),
)

export const TextBold = memo(
  forwardRef<HTMLSpanElement | HTMLParagraphElement, Omit<TextProps, 'bold'>>(
    (props, ref) => <Text {...props} {...{ bold: true, ref }} />,
  ),
)
